import React from 'react';
import { useTranslation } from 'react-i18next';
import { C, getTradePalette } from '../albumColors';
import { VISIBLE_TOTAL } from '../albumData';
import { useAlbumDashboard } from '../AlbumDashboardContext';
import QuickAddBar from './QuickAddBar';
import { buildGoMessagesPath } from '../../utils/redirect';

const openChatWithUser = (userId) => {
  if (!userId) return;
  window.location.href = buildGoMessagesPath({ sellerId: userId });
};

function ActionBtn({ icon, label, color, onClick, disabled }) {
  if (disabled) {
    return (
      <span
        aria-disabled="true"
        title={label}
        className="flex items-center justify-center gap-1.5 min-h-[44px] min-w-[44px] mobile-md:min-h-0 mobile-md:min-w-0 px-2 mobile-md:px-3 py-1.5 rounded-lg cursor-not-allowed select-none"
        style={{ color: 'var(--md-sys-color-outline, #9ca3af)', opacity: 0.55 }}
      >
        <span className="material-symbols-outlined text-lg">{icon}</span>
        <span className="hidden mobile-md:inline text-xs font-bold">{label}</span>
      </span>
    );
  }
  return (
    <a
      onClick={onClick}
      className="flex items-center justify-center gap-1.5 min-h-[44px] min-w-[44px] mobile-md:min-h-0 mobile-md:min-w-0 px-2 mobile-md:px-3 py-1.5 rounded-lg transition-all cursor-pointer"
      style={{ color }}
      onMouseEnter={e => e.currentTarget.style.backgroundColor = `${color}1a`}
      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
    >
      <span className="material-symbols-outlined text-lg">{icon}</span>
      <span className="hidden mobile-md:inline text-xs font-bold">{label}</span>
    </a>
  );
}

function AlbumActionBar({ state, dispatch, ownedCount, onImport, onSetOwned, onRemove, onAcceptTrade, onRejectTrade, onCancelOutgoing, onSendBuilderTrade, onPublishOffer, theme, onOpenPanel, selectionMode = 'chips-bar', stickers = [], onUndo, onRedo, undoLabel, redoLabel, canUndo = false, canRedo = false, onExport, chrome, chromeVariantName, onCycleChrome, onStickerAdded, onNavigateToCountry, mobileQuickAdd = false }) {
  const { t } = useTranslation();
  const { viewMode, selected, browsingUser, pendingSelection } = state;
  const pct = Math.round((ownedCount / VISIBLE_TOTAL) * 100);
  const { expanded: dashboardExpanded } = useAlbumDashboard();

  /* ── MY_ALBUM action bar (default) ───────────────────────────────── */
  if (viewMode === 'MY_ALBUM') {
    const barBg     = theme?.actionBarBg     ?? C.surfaceContainer;
    const barText   = theme?.actionBarText   ?? C.onSurface;
    const barBorder = theme?.actionBarBorder ?? `${C.outlineVariant}1a`;
    const barBackdropFilter = theme?.actionBarBackdropFilter;
    const barShadow = theme?.actionBarShadow ?? '0 1px 2px rgba(0,0,0,.05)';
    const accent      = theme?.primaryAccent    ?? C.primary;
    const accentDim   = theme?.primaryAccentDim ?? `${C.outlineVariant}4d`;
    const muted       = theme?.mutedText        ?? `${C.onSurfaceVariant}b3`;
    const track       = theme?.progressTrack    ?? `${C.outlineVariant}33`;
    const dividerBg   = theme ? `${barBorder}` : `${C.outlineVariant}4d`;
    // Chrome variant (gold/blue/neutral) — afecta el label "SELECCIÓN", la
    // barra de progreso, el buscador y los chips. Si no se pasa `chrome`
    // caemos al accent verde (modo legacy).
    const chromeAccent = chrome?.accent ?? accent;
    const chromeGradient = chrome?.gradient || null;
    const chromeShimmer = !!chrome?.shimmer;
    return (
      <>
      <style>{`
        @keyframes cm-chrome-shift { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }
        .cm-chrome-shimmer { animation: cm-chrome-shift 6s linear infinite; }
      `}</style>
      <div
        className="px-3 mobile-md:px-6 py-3 mobile-md:py-4 flex flex-wrap items-center justify-between gap-2 mobile-md:gap-4"
        style={{
          backgroundColor: barBg,
          color: barText,
          borderBottom: `1px solid ${barBorder}`,
          boxShadow: barShadow,
          backdropFilter: barBackdropFilter,
          WebkitBackdropFilter: barBackdropFilter,
        }}
      >
        <div className="flex items-center gap-3 mobile-md:gap-6 min-w-0 flex-1 flex-wrap">
          {/* En mobile (especialmente /album/5) escondemos el bloque
              "SELECCIÓN N items" para liberar espacio — la selección rara
              vez se usa cuando el tap directo agrega. En tablet+ se mantiene. */}
          <div className={`${mobileQuickAdd ? 'hidden mobile-md:flex' : 'flex'} flex-col items-start shrink-0`}>
            <span
              className={`text-[9px] uppercase tracking-widest font-black leading-none mb-1 ${chromeShimmer ? 'cm-chrome-shimmer' : ''}`}
              style={chromeGradient ? {
                backgroundImage: chromeGradient,
                backgroundSize: chromeShimmer ? '200% 100%' : '100% 100%',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              } : { color: chromeAccent }}
            >
              {t('Selección')}
            </span>
            <span className="text-sm font-bold">{t('{{n}} items', { n: selected.size })}</span>
          </div>
          {selected.size === 0 && !mobileQuickAdd && (
            <span
              className="mobile-md:hidden flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold"
              style={{ backgroundColor: `${accent}26`, color: accent, border: `1px solid ${accent}66` }}
            >
              <span className="material-symbols-outlined text-base">touch_app</span>
              {t('Tocá las figuritas')}
            </span>
          )}
          {mobileQuickAdd && (
            <div className="mobile-md:hidden flex-1 min-w-0">
              <QuickAddBar
                stickers={stickers}
                selected={selected}
                dispatch={dispatch}
                theme={theme}
                chrome={chrome}
                onAdded={onStickerAdded}
                onNavigateToCountry={onNavigateToCountry}
                navigateOnPrefix
                closeOnCommit
                wrapperClassName="relative w-full"
              />
            </div>
          )}
          <div className="hidden mobile-md:block h-8 w-px shrink-0" style={{ backgroundColor: dividerBg }} />

          {/* Inline selection actions — desktop only (mobile uses the floating bottom bar).
              Cuando no hay selección, los botones se muestran disabled y aparece un hint. */}
          {selected.size === 0 ? (
            !dashboardExpanded && (
              <div
                className="hidden desktop-xl:flex items-center gap-2 shrink-0 px-3 py-1.5 rounded-lg"
                style={{ backgroundColor: `${accent}26`, color: accent, border: `1px solid ${accent}66` }}
              >
                <span className="material-symbols-outlined text-base">touch_app</span>
                <span className="text-[12px] font-bold whitespace-nowrap">{t('Tocá las figuritas para agregar o quitar')}</span>
              </div>
            )
          ) : (
            <div className="hidden mobile-md:flex items-center gap-1 shrink-0 flex-wrap">
              <ActionBtn icon="add_circle" label={t('Agregar')} color={accent} onClick={onSetOwned} />
              <ActionBtn icon="remove_circle" label={t('Eliminar')} color={C.error} onClick={onRemove} />
              <ActionBtn icon="delete_sweep" label={t('Limpiar')} color={C.error} onClick={() => dispatch({ type: 'CLEAR_SELECTION' })} />
            </div>
          )}

          {/* Quick-Add input inline (siempre visible) + Undo/Redo.
              Sin divider para que el buscador no quede aislado. */}
          <div className="hidden tablet-sm:flex items-center gap-1 shrink-0">
            <QuickAddBar
              stickers={stickers}
              selected={selected}
              dispatch={dispatch}
              theme={theme}
              chrome={chrome}
              onAdded={onStickerAdded}
              onNavigateToCountry={onNavigateToCountry}
            />
            <button
              type="button"
              onClick={onUndo}
              disabled={!canUndo}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ color: barText, backgroundColor: 'transparent' }}
              title={canUndo ? t('Deshacer {{label}}', { label: undoLabel || '' }) : t('No hay nada para deshacer')}
              aria-label={t('Deshacer')}
            >
              <span className="material-symbols-outlined text-base">undo</span>
              {canUndo && undoLabel && (
                <span className="text-[11px] font-bold hidden desktop-xl:inline truncate max-w-[120px]">{undoLabel}</span>
              )}
            </button>
            <button
              type="button"
              onClick={onRedo}
              disabled={!canRedo}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ color: barText, backgroundColor: 'transparent' }}
              title={canRedo ? t('Rehacer {{label}}', { label: redoLabel || '' }) : t('No hay nada para rehacer')}
              aria-label={t('Rehacer')}
            >
              <span className="material-symbols-outlined text-base">redo</span>
            </button>
          </div>

        </div>

        <div className="flex items-center gap-2 mobile-md:gap-4 flex-wrap">
          <div className="text-right">
            <div className="flex items-center gap-2 mb-1 justify-end">
              <span
                className={`text-xs font-bold tabular-nums ${chromeShimmer ? 'cm-chrome-shimmer' : ''}`}
                style={chromeGradient ? {
                  backgroundImage: chromeGradient,
                  backgroundSize: chromeShimmer ? '200% 100%' : '100% 100%',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                } : { color: chromeAccent }}
              >
                {ownedCount} / {VISIBLE_TOTAL}
              </span>
              <span className="hidden mobile-md:inline text-[10px]" style={{ color: muted }}>{pct}% {t('Completo')}</span>
            </div>
            <div
              className={`${mobileQuickAdd ? 'hidden mobile-md:block' : 'block'} h-1.5 w-20 mobile-md:w-32 rounded-full overflow-hidden`}
              style={{ backgroundColor: track }}
            >
              <div
                className={`h-full rounded-full ${chromeShimmer ? 'cm-chrome-shimmer' : ''}`}
                style={{
                  width: `${pct}%`,
                  background: chromeGradient || chromeAccent,
                  backgroundSize: chromeShimmer ? '200% 100%' : '100% 100%',
                }}
              />
            </div>
          </div>
          <div className="hidden mobile-md:block h-8 w-px" style={{ backgroundColor: dividerBg }} />
          <button
            onClick={onOpenPanel}
            title={t('Coleccionistas y propuestas')}
            className="tablet-sm:hidden flex items-center justify-center gap-1.5 min-h-[44px] min-w-[44px] mobile-md:min-h-0 mobile-md:min-w-0 px-2 mobile-md:px-3 py-1.5 rounded-lg transition-all cursor-pointer"
            style={{ backgroundColor: theme ? accentDim : `${C.primary}1a`, color: accent }}
          >
            <span className="material-symbols-outlined text-lg">groups</span>
            <span className="hidden mobile-md:inline text-xs font-bold">{t('Coleccionistas')}</span>
          </button>
          {(onImport || onExport) && (
            <div className="flex items-center gap-0.5">
              {onImport && (
                <button
                  type="button"
                  onClick={onImport}
                  title={t('Importar de figuritas.app')}
                  aria-label={t('Importar de figuritas.app')}
                  className="flex items-center justify-center p-1.5 rounded-lg transition-colors"
                  style={{ color: barText, backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${accent}1a`)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <span className="material-symbols-outlined text-lg">file_download</span>
                </button>
              )}
              {onExport && (
                <button
                  type="button"
                  onClick={onExport}
                  title={t('Exportar progreso')}
                  aria-label={t('Exportar progreso')}
                  className="flex items-center justify-center p-1.5 rounded-lg transition-colors"
                  style={{ color: barText, backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${accent}1a`)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <span className="material-symbols-outlined text-lg">file_upload</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Floating bottom action bar — mobile only, shown while a selection exists.
          Sits above the mobile bottom tab bar (≈64px tall) plus the safe-area inset. */}
      {selected.size > 0 && (
        <div
          className="mobile-md:hidden fixed inset-x-0 z-[55] flex items-stretch gap-2 px-3 py-2 border-t shadow-lg"
          style={{
            bottom: 'calc(64px + var(--safe-area-bottom))',
            backgroundColor: barBg,
            borderColor: barBorder,
            color: barText,
            backdropFilter: barBackdropFilter,
            WebkitBackdropFilter: barBackdropFilter,
          }}
        >
          <button
            onClick={onSetOwned}
            className="flex-1 min-h-[48px] flex items-center justify-center gap-1.5 rounded-lg font-bold text-sm"
            style={{ backgroundColor: accent, color: '#fff' }}
          >
            <span className="material-symbols-outlined text-lg">add_circle</span>
            {t('Agregar')} ({selected.size})
          </button>
          <button
            onClick={onRemove}
            className="min-h-[48px] min-w-[48px] flex items-center justify-center rounded-lg"
            style={{ backgroundColor: `${C.error}1a`, color: C.error }}
            title={t('Eliminar')}
            aria-label={t('Eliminar')}
          >
            <span className="material-symbols-outlined text-lg">remove_circle</span>
          </button>
          <button
            onClick={() => dispatch({ type: 'CLEAR_SELECTION' })}
            className="min-h-[48px] min-w-[48px] flex items-center justify-center rounded-lg"
            style={{ backgroundColor: `${muted}1a`, color: muted }}
            title={t('Limpiar')}
            aria-label={t('Limpiar selección')}
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>
      )}
      </>
    );
  }

  /* ── USER_ALBUM action bar ───────────────────────────────────────── */
  if (viewMode === 'USER_ALBUM') {
    const theirOwned = state.theirStickers.filter(s => s.owned).length;
    const theirPct = Math.round((theirOwned / VISIBLE_TOTAL) * 100);
    const pendingCount = pendingSelection?.size || 0;
    // Bar siempre blanco/neutral, ignorando override del variant del álbum.
    const uaBg     = 'var(--md-sys-color-surface, #ffffff)';
    const uaText   = 'var(--md-sys-color-on-surface, #111827)';
    const uaBorder = 'var(--md-sys-color-outline-variant, rgba(0,0,0,0.10))';
    const uaAccent = theme?.userAlbumAccent    ?? C.otherUser;
    const uaTrack  = theme?.progressTrack      ?? `${uaAccent}33`;
    return (
      <div className="flex flex-col">
        <div
          className="px-3 mobile-md:px-6 py-3 mobile-md:py-4 flex flex-wrap items-center justify-between gap-2 mobile-md:gap-4"
          style={{
            backgroundColor: uaBg,
            color: uaText,
            borderBottom: `1px solid ${uaBorder}`,
            boxShadow: '0 1px 2px rgba(0,0,0,.05)',
          }}
        >
          <div className="flex items-center gap-4">
            <a
              onClick={() => dispatch({ type: 'VIEW_MY_ALBUM' })}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg cursor-pointer transition-all"
              style={{ color: uaText }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = `${uaText}1a`}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <span className="material-symbols-outlined text-lg">arrow_back</span>
              <span className="text-xs font-bold">{t('Mi álbum')}</span>
            </a>
            <div className="h-8 w-px" style={{ backgroundColor: uaBorder }} />
            <a
              href={browsingUser?.id ? `/users/${browsingUser.id}` : undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-2 py-1 -mx-2 rounded-lg transition-colors hover:bg-black/5"
              style={{ color: 'inherit', textDecoration: 'none' }}
              title={browsingUser?.id ? t('Ver perfil') : undefined}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: browsingUser?.color || uaAccent }}>
                {browsingUser?.initial || '?'}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-bold">{browsingUser?.name || 'User'}</span>
                <span className="text-[10px]" style={{ color: uaAccent }}>{t('Álbum')}</span>
                {browsingUser?.id && (
                  <span className="material-symbols-outlined text-[14px]" style={{ color: uaAccent }}>open_in_new</span>
                )}
              </div>
            </a>
            {browsingUser?.id && (
              <button
                type="button"
                onClick={() => openChatWithUser(browsingUser.id)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg transition-all"
                style={{ color: uaAccent, border: `1px solid ${uaAccent}66`, backgroundColor: `${uaAccent}14` }}
                title={t('Mandar mensaje a {{name}}', { name: browsingUser?.name || '' })}
              >
                <span className="material-symbols-outlined text-base">forum</span>
                <span className="text-[11px] font-bold hidden mobile-md:inline">{t('Mensaje')}</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="flex items-center gap-2 mb-1 justify-end">
                <span className="text-xs font-bold" style={{ color: uaAccent }}>{theirOwned} / {VISIBLE_TOTAL}</span>
                <span className="text-[10px]" style={{ color: uaText, opacity: 0.7 }}>{theirPct}% {t('Completo')}</span>
              </div>
              <div className="h-1.5 w-20 mobile-md:w-32 rounded-full overflow-hidden" style={{ backgroundColor: uaTrack }}>
                <div className="h-full rounded-full" style={{ backgroundColor: uaAccent, width: `${theirPct}%` }} />
              </div>
            </div>
            <div className="h-8 w-px" style={{ backgroundColor: uaBorder }} />
            <a
              onClick={() => dispatch({ type: 'VIEW_TRADE_MATCH' })}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg cursor-pointer font-bold text-xs transition-all"
              style={{ backgroundColor: uaAccent, color: 'white' }}
            >
              <span className="material-symbols-outlined text-sm">compare_arrows</span>
              {t('Ver match')}
            </a>
          </div>
        </div>

        {pendingCount > 0 && (
          <div
            className="px-3 mobile-md:px-6 py-2 flex items-center justify-between gap-3"
            style={{
              backgroundColor: `${C.primary}14`,
              borderBottom: `1px solid ${C.primary}33`,
            }}
          >
            <div className="flex items-center gap-2 text-[12px]" style={{ color: C.primaryDim }}>
              <span className="material-symbols-outlined text-base">stars</span>
              <span>
                {t('Tenés {{n}} figuritas seleccionadas', { n: pendingCount })}
              </span>
            </div>
            <button
              onClick={() => dispatch({ type: 'VIEW_TRADE_MATCH' })}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
              style={{ backgroundColor: C.primary, color: 'white' }}
            >
              <span className="material-symbols-outlined text-sm">auto_awesome</span>
              {t('Aplicar al match')}
            </button>
          </div>
        )}
      </div>
    );
  }

  /* ── TRADE_MATCH action bar ──────────────────────────────────────── */
  if (viewMode === 'TRADE_MATCH') {
    const { offering, requesting } = state.tradeBuilder;
    const tradePalette = getTradePalette(theme);
    // Header siempre blanco/neutral, independiente del variant del álbum.
    const tmBg     = 'var(--md-sys-color-surface, #ffffff)';
    const tmText   = 'var(--md-sys-color-on-surface, #111827)';
    const tmBorder = 'var(--md-sys-color-outline-variant, rgba(0,0,0,0.10))';
    const tmReceiveBg = tradePalette.receive.bg ?? '#F59E0B';
    const tmReceiveText = tradePalette.receive.text ?? '#ffffff';
    const tmReceiveStrong = tradePalette.receive.strong ?? '#B45309';
    const tmGiveBg = tradePalette.give.bg ?? theme?.primaryAccent ?? C.primary;
    const tmGiveStrong = tradePalette.give.strong ?? theme?.mutedText ?? C.primaryDim;
    const tmBothBg = tradePalette.both.bg ?? '#94A3B8';
    const tmBothStrong = tradePalette.both.strong ?? tmText;
    const neutralIcon = theme?.mutedText ?? C.onSurfaceVariant;
    return (
      <div
        className="px-2 mobile-md:px-6 py-2 mobile-md:py-4 flex items-center justify-between gap-1 mobile-md:gap-4"
        style={{
          backgroundColor: tmBg,
          color: tmText,
          borderBottom: `1px solid ${tmBorder}`,
          boxShadow: '0 1px 2px rgba(0,0,0,.05)',
        }}
      >
        <div className="flex items-center gap-1 mobile-md:gap-4 min-w-0 flex-1">
          {/* Atrás vuelve a MI álbum. En mobile sólo ícono — la pill del
              usuario al lado da contexto suficiente. */}
          <a
            onClick={() => dispatch({ type: 'VIEW_MY_ALBUM' })}
            className="flex items-center gap-1.5 px-2 mobile-md:px-3 py-1.5 rounded-lg cursor-pointer transition-all shrink-0"
            style={{ color: neutralIcon }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = `${neutralIcon}1a`}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            title={t('Mi álbum')}
            aria-label={t('Mi álbum')}
          >
            <span className="material-symbols-outlined text-lg">arrow_back</span>
            <span className="text-xs font-bold hidden mobile-md:inline">{t('Mi álbum')}</span>
          </a>
          <a
            onClick={() => dispatch({ type: 'VIEW_USER_ALBUM', user: browsingUser, stickers: state.theirStickers })}
            className="flex items-center gap-1.5 px-2 mobile-md:px-3 py-1.5 rounded-lg cursor-pointer transition-all min-w-0"
            style={{ color: neutralIcon, border: `1px solid ${tmBorder}` }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = `${neutralIcon}1a`}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            title={t('Ver álbum de {{name}}', { name: browsingUser?.name || '' })}
          >
            <span className="material-symbols-outlined text-base shrink-0">collections_bookmark</span>
            <span className="text-xs font-bold truncate hidden mobile-md:inline">
              {t('Ver álbum de {{name}}', { name: browsingUser?.name || '' })}
            </span>
            <span className="text-xs font-bold truncate mobile-md:hidden">
              {browsingUser?.name || ''}
            </span>
          </a>
          {browsingUser?.id && (
            <a
              href={`/users/${browsingUser.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg transition-all shrink-0"
              style={{ color: neutralIcon, textDecoration: 'none' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = `${neutralIcon}1a`}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              title={t('Ver perfil')}
              aria-label={t('Ver perfil')}
            >
              <span className="material-symbols-outlined text-base">person</span>
              <span className="text-[11px] font-bold hidden mobile-md:inline">{t('Perfil')}</span>
            </a>
          )}
          {browsingUser?.id && (
            <button
              type="button"
              onClick={() => openChatWithUser(browsingUser.id)}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg transition-all shrink-0"
              style={{ color: neutralIcon }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = `${neutralIcon}1a`}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              title={t('Mandar mensaje a {{name}}', { name: browsingUser?.name || '' })}
              aria-label={t('Mandar mensaje a {{name}}', { name: browsingUser?.name || '' })}
            >
              <span className="material-symbols-outlined text-base">forum</span>
              <span className="text-[11px] font-bold hidden mobile-md:inline">{t('Mensaje')}</span>
            </button>
          )}
          {/* La leyenda de colores sólo cabe en tablet+. En mobile ya está
              repetida en el bottom TradeSummaryBar ("Pides / Ofreces"). */}
          <div className="hidden mobile-md:flex items-center gap-4 flex-wrap pl-4 border-l" style={{ borderColor: tmBorder }}>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: tmReceiveBg }} />
              <span className="text-[11px] font-bold" style={{ color: tmReceiveStrong }}>{t('Ellos tienen, vos necesitás')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: tmGiveBg }} />
              <span className="text-[11px] font-bold" style={{ color: tmGiveStrong }}>{t('Vos tenés, ellos necesitan')}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 mobile-md:gap-4 shrink-0">
          {(offering.size > 0 || requesting.size > 0) && (
            <>
              <div className="text-right hidden mobile-md:block">
                <span className="text-[10px] font-bold" style={{ color: tmGiveStrong }}>{t('Ofreces')} {offering.size}</span>
                <span className="text-[10px] mx-1 opacity-40">|</span>
                <span className="text-[10px] font-bold" style={{ color: tmReceiveStrong }}>{t('Pides')} {requesting.size}</span>
              </div>
              <a
                onClick={() => dispatch({ type: 'VIEW_TRADE_REVIEW', trade: {
                  id: 'builder',
                  fromUser: browsingUser,
                  stickersOffered: [...offering],
                  stickersRequested: [...requesting],
                } })}
                className="flex items-center gap-1.5 px-2 mobile-md:px-3 py-2 rounded-lg cursor-pointer font-bold text-xs transition-all shrink-0"
                style={{ backgroundColor: tmReceiveBg, color: tmReceiveText }}
                title={t('Revisar')}
              >
                <span className="material-symbols-outlined text-sm">rate_review</span>
                <span className="hidden mobile-md:inline">{t('Revisar')}</span>
              </a>
            </>
          )}
          <a
            onClick={() => dispatch({ type: 'VIEW_MY_ALBUM' })}
            className="flex items-center gap-1.5 px-2 mobile-md:px-3 py-1.5 rounded-lg cursor-pointer transition-all shrink-0"
            style={{ color: tmText }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = `${tmBorder}`}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            title={t('Salir')}
            aria-label={t('Salir')}
          >
            <span className="material-symbols-outlined text-sm">close</span>
            <span className="text-xs font-bold hidden mobile-md:inline">{t('Salir')}</span>
          </a>
        </div>
      </div>
    );
  }

  /* ── OFFER_BUILDER action bar ────────────────────────────────────── */
  if (viewMode === 'OFFER_BUILDER') {
    return (
      <div className="px-3 mobile-md:px-6 py-3 mobile-md:py-4 flex flex-wrap items-center justify-between gap-2 mobile-md:gap-4 bg-surface border-b border-outline">
        <div className="flex items-center gap-3 min-w-0">
          <a
            onClick={() => dispatch({ type: 'VIEW_MY_ALBUM' })}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg cursor-pointer transition-colors text-on-surface-variant hover:bg-surface-container shrink-0"
          >
            <span className="material-symbols-outlined text-lg">arrow_back</span>
            <span className="text-xs font-bold">{t('Volver')}</span>
          </a>
          <div className="min-w-0">
            <p className="text-sm font-bold text-on-surface truncate">{t('Crear oferta pública')}</p>
            <p className="text-[10px] text-on-surface-variant truncate">
              {t('Tocá tus repetidas para ofrecer y tus faltantes para pedir.')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  /* ── TRADE_REVIEW action bar ─────────────────────────────────────── */
  if (viewMode === 'TRADE_REVIEW') {
    const trade = state.activeTrade;
    const isOutgoing = trade?.direction === 'outgoing';
    const isBuilder = trade?.id === 'builder' || trade?.id === 'offer-builder';
    const isOfferBuilder = trade?.id === 'offer-builder';
    const otherName = trade?.otherUser?.name || trade?.fromUser?.name || '';
    const otherUserId = trade?.otherUser?.id || trade?.fromUser?.id;
    const giveCount = (trade?.youGive ?? trade?.stickersOffered ?? []).length;
    const receiveCount = (trade?.youReceive ?? trade?.stickersRequested ?? []).length;
    const accent     = theme?.primaryAccent ?? C.primary;
    const accentDim  = theme?.mutedText     ?? C.primaryDim;
    const tradePalette = getTradePalette(theme);
    const receiveBg = tradePalette.receive.bg ?? accent;
    const receiveText = tradePalette.receive.text ?? '#ffffff';
    const receiveStrong = tradePalette.receive.strong ?? accentDim;
    const giveBg = tradePalette.give.bg ?? '#EF4444';
    const giveText = tradePalette.give.text ?? '#ffffff';
    const giveStrong = tradePalette.give.strong ?? '#991b1b';
    // Header bar blanco/neutral, independiente del variant del álbum.
    // Las dots de Recibís/Das se mantienen verde/rojo (consistente con los chips
    // internos del trade).
    const headerAccent = 'var(--md-sys-color-primary, #2563eb)';
    const reviewBg     = 'var(--md-sys-color-surface, #ffffff)';
    const reviewText   = 'var(--md-sys-color-on-surface, #111827)';
    const reviewBorder = 'var(--md-sys-color-outline-variant, rgba(0,0,0,0.10))';
    const recibisColor = '#16a34a';
    const recibisDim   = '#15803d';
    const dasColor     = '#ef4444';
    const dasDim       = '#b91c1c';
    return (
      <div
        className="px-3 mobile-md:px-6 py-3 mobile-md:py-4 flex flex-wrap items-center justify-between gap-2 mobile-md:gap-4"
        style={{
          backgroundColor: reviewBg,
          color: reviewText,
          borderBottom: `1px solid ${reviewBorder}`,
          boxShadow: '0 1px 2px rgba(0,0,0,.05)',
        }}
      >
        <div className="flex items-center gap-4">
          <a
            onClick={() => isOfferBuilder
              ? dispatch({ type: 'VIEW_OFFER_BUILDER' })
              : isBuilder
                ? dispatch({ type: 'VIEW_TRADE_MATCH' })
                : dispatch({ type: 'VIEW_MY_ALBUM' })}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg cursor-pointer transition-all"
            style={{ color: headerAccent }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(37,99,235,0.10)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span className="material-symbols-outlined text-lg">arrow_back</span>
            <span className="text-xs font-bold">{isBuilder ? t('Editar') : t('Volver')}</span>
          </a>
          <div className="h-8 w-px" style={{ backgroundColor: isBuilder ? `${accent}40` : reviewBorder }} />
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold">
              {isOfferBuilder
                ? t('Oferta pública')
                : isBuilder
                  ? t('Revisar propuesta a {{name}}', { name: otherName })
                  : isOutgoing
                    ? t('Tu propuesta a {{name}}', { name: otherName })
                    : t('Intercambio con {{name}}', { name: otherName })}
            </span>
            {otherUserId && (
              <a
                href={`/users/${otherUserId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-1.5 py-0.5 rounded transition-all"
                style={{ color: 'inherit', textDecoration: 'none', opacity: 0.7 }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                title={t('Ver perfil')}
              >
                <span className="material-symbols-outlined text-sm">person</span>
              </a>
            )}
          </div>
          <div className="flex items-center gap-4 ml-4">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: recibisColor }} />
              <span className="text-[11px] font-bold" style={{ color: recibisDim }}>{t('Recibís')} ({receiveCount})</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: dasColor }} />
              <span className="text-[11px] font-bold" style={{ color: dasDim }}>{t('Das')} ({giveCount})</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {trade?.id && !isBuilder && !isOutgoing && (
            <>
              <a
                onClick={() => onRejectTrade(trade.id)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg cursor-pointer font-bold text-xs transition-all"
                style={{ backgroundColor: giveBg, color: giveText }}
              >
                <span className="material-symbols-outlined text-sm">close</span>
                {t('Rechazar')}
              </a>
              <a
                onClick={() => onAcceptTrade(trade.id)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg cursor-pointer font-bold text-xs transition-all"
                style={{ backgroundColor: receiveBg, color: receiveText }}
              >
                <span className="material-symbols-outlined text-sm">check</span>
                {t('Aceptar')}
              </a>
            </>
          )}
          {/* PENDING outgoing: Cancelar propuesta. Antes vivía dentro del
              TradeContextBanner; lo movimos acá para no duplicar y unificar
              todas las acciones de PENDING en este header. */}
          {trade?.id && !isBuilder && isOutgoing && (trade.status === 'PENDING' || trade.status == null) && onCancelOutgoing && (
            <a
              onClick={() => onCancelOutgoing(trade.id)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg cursor-pointer font-bold text-xs transition-all"
              style={{ backgroundColor: giveBg, color: giveText }}
            >
              <span className="material-symbols-outlined text-sm">cancel</span>
              {t('Cancelar propuesta')}
            </a>
          )}
          {isBuilder && (
          <a
            onClick={() => isOfferBuilder
              ? (onPublishOffer && onPublishOffer())
              : (onSendBuilderTrade && onSendBuilderTrade())}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg cursor-pointer font-bold text-xs transition-all"
            style={{ backgroundColor: receiveBg, color: receiveText, boxShadow: `0 4px 12px -4px ${receiveBg}` }}
          >
            <span className="material-symbols-outlined text-sm">send</span>
            {isOfferBuilder ? t('Publicar oferta') : t('Enviar propuesta')}
            </a>
          )}
        </div>
      </div>
    );
  }

  /* ── OFFER_REVIEW action bar ─────────────────────────────────────── */
  if (viewMode === 'OFFER_REVIEW') {
    const offer = state.activeTrade;
    const giveCount = (offer?.youGive ?? []).length;
    const receiveCount = (offer?.youReceive ?? []).length;
    const headerAccent = 'var(--md-sys-color-primary, #2563eb)';
    const reviewBg     = 'var(--md-sys-color-surface, #ffffff)';
    const reviewText   = 'var(--md-sys-color-on-surface, #111827)';
    const reviewBorder = 'var(--md-sys-color-outline-variant, rgba(0,0,0,0.10))';
    return (
      <div
        className="px-3 mobile-md:px-6 py-3 mobile-md:py-4 flex flex-wrap items-center justify-between gap-2 mobile-md:gap-4"
        style={{
          backgroundColor: reviewBg,
          color: reviewText,
          borderBottom: `1px solid ${reviewBorder}`,
          boxShadow: '0 1px 2px rgba(0,0,0,.05)',
        }}
      >
        <div className="flex items-center gap-4">
          <a
            onClick={() => dispatch({ type: 'VIEW_MY_ALBUM' })}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg cursor-pointer transition-all"
            style={{ color: headerAccent }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(37,99,235,0.10)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span className="material-symbols-outlined text-lg">arrow_back</span>
            <span className="text-xs font-bold">{t('Volver')}</span>
          </a>
          <div className="h-8 w-px" style={{ backgroundColor: reviewBorder }} />
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-base" style={{ color: headerAccent }}>campaign</span>
            <span className="text-sm font-bold">{t('Tu oferta')}</span>
          </div>
          <div className="flex items-center gap-4 ml-4">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#ef4444' }} />
              <span className="text-[11px] font-bold" style={{ color: '#b91c1c' }}>{t('Das')} ({giveCount})</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#16a34a' }} />
              <span className="text-[11px] font-bold" style={{ color: '#15803d' }}>{t('Recibís')} ({receiveCount})</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default AlbumActionBar;
